/**
 * Celebration SFX — gated playSfx (Plan 02-15 / CEL-02 / PITFALLS MED-1).
 *
 * Plays a short bundled WAV from `apps/web/public/sfx/`. Honours three
 * gates per UI-SPEC §"Sound bundle":
 *
 *   1. opts.reducedMotion === true
 *      User has `prefers-reduced-motion: reduce`. Sound is part of the
 *      celebration motion contract; silencing matches the static fallback.
 *
 *   2. opts.mutedByGlobal === true
 *      Admin has flipped `app_config.celebration_sounds_enabled` off.
 *
 *   3. navigator.userActivation.hasBeenActive === false
 *      iOS / fresh tablet boot — autoplay is blocked until the user has
 *      interacted with the page. Apple/Chromium reject `audio.play()` with
 *      a NotAllowedError; we no-op silently rather than logging noise.
 *      If `userActivation` is missing entirely (older Firefox, some
 *      jsdom configurations) we DO play — only the explicit `false` is
 *      a gate. False positives over false negatives.
 *
 * Implementation: one HTMLAudioElement per call. Files are tiny (≤ 30KB
 * total bundle, precached by the SW), and the cost of constructing a new
 * Audio object is sub-millisecond. Web Audio API ceremony adds zero value
 * for v1.
 *
 * The `audio.play()` Promise rejection is swallowed — autoplay policies
 * vary across browsers, and a rejected play is not a bug we need to
 * surface.
 */
const SFX_PATHS = {
  'lock-in': '/sfx/lock-in.wav',
  done: '/sfx/done.wav',
  approved: '/sfx/approved.wav',
  'achievement-takeover': '/sfx/achievement-takeover.wav',
} as const;

export type SfxName = keyof typeof SFX_PATHS;

export interface PlaySfxOptions {
  /** True when the user has prefers-reduced-motion: reduce. */
  reducedMotion?: boolean;
  /** True when admin has globally muted celebration sounds. */
  mutedByGlobal?: boolean;
}

interface MaybeUserActivation {
  hasBeenActive?: boolean;
}

interface NavigatorWithUserActivation {
  userActivation?: MaybeUserActivation;
}

/**
 * Fire-and-forget. Returns nothing — callers don't need to know whether
 * the gate fired. The function is safe to call from any user-gesture
 * handler or WS event handler.
 */
export function playSfx(name: SfxName, opts: PlaySfxOptions = {}): void {
  // Gate 1: reduced motion.
  if (opts.reducedMotion === true) return;

  // Gate 2: global mute.
  if (opts.mutedByGlobal === true) return;

  // Gate 3: iOS user-activation. Only an explicit `false` blocks; missing
  // userActivation (older browsers, test environments) is permitted.
  if (typeof globalThis.navigator !== 'undefined') {
    const nav = globalThis.navigator as unknown as NavigatorWithUserActivation;
    if (
      nav.userActivation !== undefined &&
      nav.userActivation !== null &&
      nav.userActivation.hasBeenActive === false
    ) {
      return;
    }
  }

  // Defence-in-depth: if Audio is missing (server-side render, exotic
  // jsdom config), no-op silently. Reference via globalThis so the lint
  // doesn't need a per-browser-global allow-list entry. We deliberately
  // narrow only to the `play` method we use rather than naming
  // HTMLAudioElement (avoids adding another global to eslint config).
  type AudioLike = { play: () => Promise<void> | undefined };
  const AudioCtor = (
    globalThis as unknown as {
      Audio?: new (src?: string) => AudioLike;
    }
  ).Audio;
  if (typeof AudioCtor !== 'function') return;

  try {
    const audio = new AudioCtor(SFX_PATHS[name]);
    const result = audio.play();
    // Swallow autoplay-policy rejections without noise. Some browsers
    // return a synchronous undefined; guard accordingly.
    if (result && typeof (result as Promise<void>).catch === 'function') {
      (result as Promise<void>).catch(() => {
        /* autoplay blocked or media error — silently drop */
      });
    }
  } catch {
    /* construction failed — silently drop */
  }
}
